type AnyRecord = Record<string, any>;

const BOT_ONLY_KEYS = new Set(['simulation_name', 'attack_id', 'flags', 'command']);
const ATTACK_FLAG_SKIP_KEYS = new Set(['description', 'flags', 'attack_id', 'simulation_name', 'command']);

const stripNestedFlags = (value: any, keepFlagsKey = false): any => {
  if (Array.isArray(value)) {
    return value.map((item) => stripNestedFlags(item, keepFlagsKey));
  }
  if (value && typeof value === 'object') {
    const cleaned: AnyRecord = {};
    Object.entries(value).forEach(([key, item]) => {
      if (key === 'flags' && !keepFlagsKey) {
        return;
      }
      cleaned[key] = stripNestedFlags(item, keepFlagsKey);
    });
    return cleaned;
  }
  return value;
};

const extractAttackId = (attackTypeId?: string, config?: AnyRecord): number | null => {
  const rawId = config?.attack_id;
  if (typeof rawId === 'number') return rawId;
  if (typeof rawId === 'string' && /^\d+$/.test(rawId)) return Number.parseInt(rawId, 10);

  if (typeof attackTypeId === 'string' && attackTypeId.includes('_')) {
    const last = attackTypeId.split('_').pop();
    if (last && /^\d+$/.test(last)) {
      return Number.parseInt(last, 10);
    }
  }
  return null;
};

const assignIfDefined = (target: AnyRecord, key: string, value: any) => {
  if (value !== undefined) {
    target[key] = stripNestedFlags(value);
  }
};

// SSL/TLS Flood (attack_id 61) exposes its SSL settings as flat boolean toggles
// because the schema-driven form can only render typed controls for flat fields.
// The backend folds them into the nested `flags["61"].ssl_options` dict the bot
// reads (attack_management.py, attack_id == 61), so the way back has to unfold
// them again: leaving the nested object in the UI config both hides the saved
// toggle state from the form and gets copied verbatim over the freshly assembled
// ssl_options on the next save (CAO-238).
const SSL_OPTION_UI_KEYS: Record<string, string> = {
  enabled: 'ssl_enabled',
  verify_cert: 'ssl_verify_cert',
  verify_hostname: 'ssl_verify_hostname',
  use_sni: 'ssl_use_sni',
};

// Only attack 61 exposes the flat toggles. Slow POST (31), Slowloris (32) and
// the other TLS-capable payloads read their own `flags[<id>].ssl_options` with
// no UI field behind it, so theirs has to survive the round trip as the nested
// dict the bot expects -- unfolding it there would silently drop it.
const SSL_OPTIONS_ATTACK_ID = 61;

const applySslOptionMappings = (normalized: AnyRecord, sslOptions: any) => {
  if (!sslOptions || typeof sslOptions !== 'object' || Array.isArray(sslOptions)) return;
  Object.entries(SSL_OPTION_UI_KEYS).forEach(([botKey, uiKey]) => {
    if (botKey in sslOptions && normalized[uiKey] === undefined) {
      normalized[uiKey] = Boolean(sslOptions[botKey]);
    }
  });
};

/**
 * Unfold a nested `ssl_options` object left in a *UI-shaped* configuration.
 *
 * Configurations saved before CAO-238 carry the bot-side dict (the Operations
 * panel wrote back what it read from S3), which the form cannot edit. Flat keys
 * already present win; the nested key is dropped so it can never outrank them.
 *
 * Scoped to attack 61 by `attackTypeId`. When the attack type is unknown the
 * config is returned untouched: the backend transform still seeds `ssl_options`
 * from the nested dict, so nothing is lost -- only the toggles stay on their
 * schema defaults until the attack type is known.
 */
export const unfoldSslOptions = (config: AnyRecord | undefined, attackTypeId?: string): AnyRecord => {
  if (!config || typeof config !== 'object') return {};
  if (!('ssl_options' in config)) return config;
  if (extractAttackId(attackTypeId, config) !== SSL_OPTIONS_ATTACK_ID) return config;

  const { ssl_options: sslOptions, ...rest } = config;
  const unfolded: AnyRecord = { ...rest };
  applySslOptionMappings(unfolded, sslOptions);
  return unfolded;
};

const applyCarpetBombingMappings = (normalized: AnyRecord, flags: AnyRecord) => {
  const flags76 = flags['76'];
  if (flags76 && typeof flags76 === 'object') {
    assignIfDefined(normalized, 'mode', flags76.mode);
    assignIfDefined(normalized, 'interval_seconds', flags76.interval_seconds);
    assignIfDefined(normalized, 'random_interval', flags76.random_interval);
    assignIfDefined(normalized, 'interval_seconds_min', flags76.interval_seconds_min);
    assignIfDefined(normalized, 'interval_seconds_max', flags76.interval_seconds_max);
    assignIfDefined(normalized, 'expand_per_target', flags76.expand_per_target);

    if (Array.isArray(flags76.attack_types)) {
      const attackTypes = flags76.attack_types
        .map((value: any) => Number.parseInt(String(value), 10))
        .filter((value: number) => Number.isFinite(value));

      normalized.include_udp_flood = attackTypes.includes(71);
      normalized.include_tcp_flood = attackTypes.includes(72);
      normalized.include_icmp_flood = attackTypes.includes(73);
      normalized.include_fragmented_udp_flood = attackTypes.includes(75);
      normalized.include_ssl_flood = attackTypes.includes(61);
      normalized.include_tcp_connection_flood = attackTypes.includes(51);
      normalized.include_get_flood = attackTypes.includes(1);
      normalized.include_post_flood = attackTypes.includes(2);
      normalized.include_slow_post = attackTypes.includes(31);
      normalized.include_slow_get = attackTypes.includes(32);
    }
  }

  const udpFlags = flags['71'];
  if (udpFlags && typeof udpFlags === 'object') {
    assignIfDefined(normalized, 'udp_ports', udpFlags.port);
    assignIfDefined(normalized, 'udp_data_size', udpFlags.data_size);
    assignIfDefined(normalized, 'attack_interval', udpFlags.attack_interval);
  }

  const tcpFlags = flags['72'];
  if (tcpFlags && typeof tcpFlags === 'object') {
    assignIfDefined(normalized, 'tcp_ports', tcpFlags.port);
    assignIfDefined(normalized, 'tcp_flags', tcpFlags.tcp_flags);
    if (normalized.attack_interval === undefined) {
      assignIfDefined(normalized, 'attack_interval', tcpFlags.attack_interval);
    }
  }

  const icmpFlags = flags['73'];
  if (icmpFlags && typeof icmpFlags === 'object') {
    assignIfDefined(normalized, 'icmp_type', icmpFlags.icmp_type);
    assignIfDefined(normalized, 'icmp_packet_size', icmpFlags.packet_size);
    if (normalized.attack_interval === undefined) {
      assignIfDefined(normalized, 'attack_interval', icmpFlags.attack_interval);
    }
  }

  const fragFlags = flags['75'];
  if (fragFlags && typeof fragFlags === 'object') {
    assignIfDefined(normalized, 'fragmented_udp_ports', fragFlags.port);
    assignIfDefined(normalized, 'fragmented_udp_data_size', fragFlags.data_size);
    assignIfDefined(normalized, 'fragment_size', fragFlags.fragment_size);
    if (normalized.attack_interval === undefined) {
      assignIfDefined(normalized, 'attack_interval', fragFlags.attack_interval);
    }
  }

  const sslFlags = flags['61'];
  if (sslFlags && typeof sslFlags === 'object') {
    assignIfDefined(normalized, 'ssl_ports', sslFlags.port);
    assignIfDefined(normalized, 'ssl_timeout', sslFlags.timeout);
  }

  const tcpConnFlags = flags['51'];
  if (tcpConnFlags && typeof tcpConnFlags === 'object') {
    assignIfDefined(normalized, 'tcp_conn_ports', tcpConnFlags.port);
    assignIfDefined(normalized, 'tcp_conn_connection_time', tcpConnFlags.connection_time);
  }

  const getFlags = flags['1'];
  if (getFlags && typeof getFlags === 'object') {
    assignIfDefined(normalized, 'get_flood_timeout', getFlags.timeout);
  }

  const postFlags = flags['2'];
  if (postFlags && typeof postFlags === 'object') {
    assignIfDefined(normalized, 'post_data_size', postFlags.data_size);
    assignIfDefined(normalized, 'post_content_type', postFlags.content_type);
  }

  const slowPostFlags = flags['31'];
  if (slowPostFlags && typeof slowPostFlags === 'object') {
    assignIfDefined(normalized, 'slowpost_content_length', slowPostFlags.content_length);
    assignIfDefined(normalized, 'slowpost_interval', slowPostFlags.interval);
    assignIfDefined(normalized, 'slowpost_send_rate', slowPostFlags.send_rate);
  }

  const slowGetFlags = flags['32'];
  if (slowGetFlags && typeof slowGetFlags === 'object') {
    assignIfDefined(normalized, 'slowloris_ports', slowGetFlags.port);
    assignIfDefined(normalized, 'slowloris_sleep_time', slowGetFlags.sleep_time);
    assignIfDefined(normalized, 'slowloris_packet_size', slowGetFlags.packet_size);
  }
};

export const normalizeS3ConfigToUi = (rawConfig: AnyRecord | undefined, attackTypeId?: string): AnyRecord => {
  if (!rawConfig || typeof rawConfig !== 'object') return {};

  const cleaned = stripNestedFlags(rawConfig, true) as AnyRecord;
  const normalized: AnyRecord = {};

  if ('targets' in cleaned) normalized.targets = cleaned.targets;
  if ('threads' in cleaned) normalized.threads = cleaned.threads;

  Object.entries(cleaned).forEach(([key, value]) => {
    if (BOT_ONLY_KEYS.has(key) || key === 'targets' || key === 'threads') {
      return;
    }
    normalized[key] = value;
  });

  const flags = cleaned.flags;
  if (flags && typeof flags === 'object') {
    const attackId = extractAttackId(attackTypeId, cleaned);
    const attackKey = attackId !== null ? String(attackId) : null;
    const attackFlags = attackKey ? flags[attackKey] : undefined;

    if (attackFlags && typeof attackFlags === 'object') {
      Object.entries(attackFlags).forEach(([key, value]) => {
        if (ATTACK_FLAG_SKIP_KEYS.has(key)) return;
        if (key === 'ssl_options' && attackId === SSL_OPTIONS_ATTACK_ID) {
          applySslOptionMappings(normalized, value);
          return;
        }
        if (!(key in normalized)) {
          normalized[key] = stripNestedFlags(value);
        }
      });
    }

    const httpFlags = flags.http;
    if (httpFlags && typeof httpFlags === 'object') {
      if ('timeout' in httpFlags && normalized.timeout === undefined) {
        normalized.timeout = httpFlags.timeout;
      }
      if ('version' in httpFlags && normalized.http_version === undefined) {
        normalized.http_version = httpFlags.version;
      }
      if ('user_agents' in httpFlags && normalized.user_agents === undefined) {
        normalized.user_agents = httpFlags.user_agents;
      }
      if ('headers' in httpFlags && normalized.custom_headers === undefined) {
        normalized.custom_headers = httpFlags.headers;
      }
      if ('post_data' in httpFlags && normalized.post_data === undefined) {
        normalized.post_data = httpFlags.post_data;
      }
      if ('content_type' in httpFlags && normalized.content_type === undefined) {
        normalized.content_type = httpFlags.content_type;
      }
      if ('methods' in httpFlags && normalized.methods === undefined) {
        normalized.methods = httpFlags.methods;
      }
      if ('follow_redirects' in httpFlags && normalized.follow_redirects === undefined) {
        normalized.follow_redirects = httpFlags.follow_redirects;
      }
      if ('cache_busting_enabled' in httpFlags && normalized.cache_busting_enabled === undefined) {
        normalized.cache_busting_enabled = httpFlags.cache_busting_enabled;
      }
      if ('cache_busting_params' in httpFlags && normalized.cache_busting_params === undefined) {
        normalized.cache_busting_params = httpFlags.cache_busting_params;
      }
    }

    if (attackId === 76) {
      applyCarpetBombingMappings(normalized, flags);
    }
  }

  return normalized;
};

/**
 * True when a stored configuration is bot-shaped rather than UI-shaped.
 *
 * A scenario's `configuration` is persisted verbatim from whatever the client
 * posted (`backend/attack_management.py`, which calls the bot-only keys out as
 * something "some UI flows may provide ... via JSON editor"), so it can be
 * either shape and every editor entry point has to tell them apart.
 */
export const isBotShapedConfig = (config: AnyRecord | undefined): boolean =>
  !!config && typeof config === 'object' && ('flags' in config || 'attack_id' in config || 'simulation_name' in config);

/**
 * The single way to turn a *stored* configuration into one the schema-driven
 * form can edit, whatever shape it was saved in.
 *
 * Bot-shaped configs are folded back onto their UI fields; UI-shaped ones still
 * go through `unfoldSslOptions`, because a config saved before CAO-238 carries
 * the bot-side `ssl_options` dict at the top level. The unfold runs on the
 * normalized result too: it is a no-op unless `ssl_options` survived, which is
 * what happens when a bot config carries one outside `flags[<id>]`.
 *
 * Every entry point that seeds `AttackConfigForm` from a saved configuration
 * must use this — skipping it leaves the SSL toggles on their schema defaults
 * and, worse, lets the next save write those defaults back over what was
 * stored (CAO-238).
 */
export const normalizeConfigurationForForm = (
  config: AnyRecord | undefined,
  attackTypeId?: string
): AnyRecord => {
  if (!config || typeof config !== 'object') return {};
  if (!isBotShapedConfig(config)) return unfoldSslOptions(config, attackTypeId);

  // `normalizeS3ConfigToUi` drops `attack_id` as a bot-only key, so resolve the
  // id from the raw config before it goes: without it the second pass has
  // nothing to scope itself by when the caller knows no attack type.
  const attackId = extractAttackId(attackTypeId, config);
  const effectiveTypeId = attackTypeId ?? (attackId !== null ? `attack_id_${attackId}` : undefined);
  return unfoldSslOptions(normalizeS3ConfigToUi(config, attackTypeId), effectiveTypeId);
};
